import React from 'react';
import { Button, Col, Form, Row, Spinner } from 'react-bootstrap';
import { Formik } from 'formik';
import { array, object, string } from 'yup';
import Select from 'react-select';
import MultiSelect from '@/components/common/MultiSelect';
import OptionLayoutPlace from '@/app/common/components/OptionsLayout/OptionLayoutPlace';
import SingleValueFlag from '@/app/common/components/OptionsLayout/SingleValueFlag';
import geoLanguagesData from '@/app/common/api/backups/languages.json';
import geoLocationsData from '@/app/common/api/backups/locations.json';
import {
  goalOptions,
  typeOptions
} from '@/app/KeywordResearch/consts/aiKeywordsFormConsts';

type SelectOption = {
  value: string;
  label: string;
};

type CountryOption = SelectOption & {
  flag?: string;
};

type AiKeywordsFormValues = {
  subject: string;
  goal: string;
  keywords: string;
  type: SelectOption[];
};

type AiKeywordsFormProps = {
  onSubmit: (values: AiKeywordsFormValues, actions: any) => Promise<void>;
  isSubmitting: boolean;
  selectedLanguage: SelectOption;
  setSelectedLanguage: React.Dispatch<React.SetStateAction<SelectOption>>;
  geoLanguages: SelectOption[];
  selectedPlace: CountryOption;
  setSelectedPlace: React.Dispatch<React.SetStateAction<CountryOption>>;
  geoLocationsCountries: CountryOption[];
};

const MultiSelectComponent: any = MultiSelect;

const AiKeywordsForm = ({
  onSubmit,
  isSubmitting,
  selectedLanguage,
  setSelectedLanguage,
  geoLanguages,
  selectedPlace,
  setSelectedPlace,
  geoLocationsCountries
}: AiKeywordsFormProps) => {
  return (
    <Row className="mb-3">
      <Col lg={12}>
        <Formik
          onSubmit={onSubmit}
          initialValues={{
            subject: '',
            goal: '',
            keywords: '',
            type: []
          }}
          validationSchema={object().shape({
            subject: string()
              .min(3, 'موضوع باید حداقل ۳ کاراکتر باشد')
              .required('وارد کردن موضوع الزامی است'),
            goal: string().required('انتخاب هدف الزامی است'),
            keywords: string()
              .min(2, 'کلمات کلیدی باید حداقل ۲ کاراکتر باشد')
              .required('وارد کردن کلمات کلیدی الزامی است'),
            type: array()
              .min(1, 'حداقل یک نوع باید انتخاب شود')
              .required('انتخاب نوع الزامی است')
          })}
        >
          {({
            values,
            errors,
            touched,
            handleChange,
            handleBlur,
            handleSubmit,
            setFieldValue,
            isSubmitting: isFormikSubmitting
          }) => (
            <Form className="mt-3 mb-2" onSubmit={handleSubmit}>
              <Row className="g-3 align-items-start">
                <Col xs={12} md={4}>
                  <Form.Group controlId="ai-subject">
                    <Form.Label className="fs--1 mb-1">عنوان سئو</Form.Label>
                    <Form.Control
                      type="text"
                      name="subject"
                      placeholder="مثال: بهترین لپ تاپ های ایسوس در بازار ایران"
                      value={values.subject}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      isInvalid={touched.subject && !!errors.subject}
                    />
                    <small
                      className={`fs--2 mt-1 d-block ${
                        touched.subject && errors.subject
                          ? 'text-danger'
                          : 'text-700'
                      }`}
                    >
                      {touched.subject && errors.subject
                        ? errors.subject
                        : 'عنوان پیشنهادی خود برای نمایش در گوگل را بنویسید'}
                    </small>
                  </Form.Group>
                </Col>

                <Col xs={12} md={4}>
                  <Form.Group controlId="ai-goal">
                    <Form.Label className="fs--1 mb-1">هدف</Form.Label>
                    <Form.Select
                      name="goal"
                      value={values.goal || ''}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      isInvalid={touched.goal && !!errors.goal}
                    >
                      <option value="">نوع آدرس</option>
                      {goalOptions.map(option => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </Form.Select>
                    <small
                      className={`fs--2 mt-1 d-block ${
                        touched.goal && errors.goal ? 'text-danger' : 'text-700'
                      }`}
                    >
                      {touched.goal && errors.goal
                        ? errors.goal
                        : 'کاربر وارد چه صفحه/آدرسی خواهد شد؟'}
                    </small>
                  </Form.Group>
                </Col>

                <Col xs={12} md={4}>
                  <Form.Group controlId="ai-keywords">
                    <Form.Label className="fs--1 mb-1">کلمات</Form.Label>
                    <Form.Control
                      type="text"
                      name="keywords"
                      placeholder="لپ تاپ گیمینگ, خرید لپ تاپ مخصوص بازی"
                      value={values.keywords}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      isInvalid={touched.keywords && !!errors.keywords}
                    />
                    <small
                      className={`fs--2 mt-1 d-block ${
                        touched.keywords && errors.keywords
                          ? 'text-danger'
                          : 'text-700'
                      }`}
                    >
                      {touched.keywords && errors.keywords
                        ? errors.keywords
                        : 'یک کلمه بنویسید؛ برای چند کلمه از ، استفاده کنید.'}
                    </small>
                  </Form.Group>
                </Col>
              </Row>

              <Row className="g-3 mt-1 align-items-start">
                <Col xs={12} md={4}>
                  <Form.Group controlId="ai-type">
                    <Form.Label className="fs--1 mb-1">نوع تحقیق</Form.Label>
                    <MultiSelectComponent
                      options={typeOptions}
                      placeholder="انتخاب نوع"
                      value={values.type}
                      onChange={(selected: any) =>
                        setFieldValue('type', selected)
                      }
                      onBlur={() => handleBlur('type')}
                      styles={{
                        control: (baseStyles: any) => ({
                          ...baseStyles,
                          direction: 'rtl !important',
                          paddingLeft: '10px',
                          paddingRight: '10px',
                          color: 'var(--falcon-emphasise) !important',
                          boxShadow: 'none !important',
                          borderRadius: '5px 5px 5px 5px !important',
                          width: '100% !important',
                          cursor: 'pointer',
                          borderColor:
                            errors.type &&
                            touched.type &&
                            'var(--falcon-form-invalid-border-color) !important'
                        }),
                        input: (baseStyles: any) => ({
                          ...baseStyles,
                          direction: 'ltr !important',
                          color: 'var(--falcon-emphasise) !important',
                          boxShadow: 'none !important'
                        })
                      }}
                      components={{
                        Option: OptionLayoutPlace
                      }}
                    />
                    <small
                      className={`fs--2 mt-1 d-block ${
                        touched.type && errors.type ? 'text-danger' : 'text-700'
                      }`}
                    >
                      {touched.type && errors.type
                        ? errors.type
                        : 'نوع تحقیق چگونه باشد؟ (گزینه مهم)'}
                    </small>
                  </Form.Group>
                </Col>

                <Col xs={12} md={4}>
                  <Form.Group controlId="ai-language">
                    <Form.Label className="fs--1 mb-1">زبان</Form.Label>
                    <Select
                      menuShouldScrollIntoView={true}
                      closeMenuOnSelect={true}
                      isSearchable={true}
                      onChange={e => e && setSelectedLanguage(e)}
                      options={geoLanguages}
                      classNamePrefix="react-select"
                      value={selectedLanguage}
                      maxMenuHeight={300}
                      styles={{
                        control: baseStyles => ({
                          ...baseStyles,
                          paddingLeft: '0px',
                          paddingRight: '10px',
                          color: 'var(--falcon-emphasise) !important',
                          boxShadow: 'none !important',
                          cursor: 'pointer',
                          borderRadius: '5px 5px 5px 5px'
                        }),
                        input: baseStyles => ({
                          ...baseStyles,
                          color: 'var(--falcon-emphasise) !important',
                          boxShadow: 'none !important'
                        })
                      }}
                      components={{
                        Option: OptionLayoutPlace,
                        SingleValue: SingleValueFlag
                      }}
                    />
                  </Form.Group>
                </Col>

                <Col
                  className="d-flex align-items-end gap-3 flex-lg-row flex-column"
                  xs={12}
                  md={4}
                >
                  <Form.Group controlId="ai-country" className="w-100">
                    <Form.Label className="fs--1 mb-1">کشور</Form.Label>
                    <Select
                      menuShouldScrollIntoView={true}
                      closeMenuOnSelect={true}
                      isSearchable={true}
                      onChange={e => {
                        if (!e) return;
                        setSelectedPlace(e);
                        if (geoLocationsData) {
                          const { official_locaguage_code } =
                            geoLocationsData.data.find(item => item.code === e.value) ||
                            {};

                          if (official_locaguage_code) {
                            const defaultLanguage = geoLanguagesData.data.find(
                              item => item.code === official_locaguage_code
                            );
                            if (defaultLanguage) {
                              setSelectedLanguage({
                                value: defaultLanguage.code,
                                label: defaultLanguage.fa_name
                              });
                            }
                          }
                        }
                      }}
                      options={geoLocationsCountries}
                      classNamePrefix="react-select"
                      value={selectedPlace}
                      maxMenuHeight={300}
                      styles={{
                        control: baseStyles => ({
                          ...baseStyles,
                          paddingLeft: '0px',
                          paddingRight: '10px',
                          color: 'var(--falcon-emphasise) !important',
                          boxShadow: 'none !important',
                          cursor: 'pointer',
                          borderRadius: '5px 5px 5px 5px'
                        }),
                        input: baseStyles => ({
                          ...baseStyles,
                          color: 'var(--falcon-emphasise) !important',
                          boxShadow: 'none !important'
                        })
                      }}
                      components={{
                        Option: OptionLayoutPlace,
                        SingleValue: SingleValueFlag
                      }}
                    />
                  </Form.Group>
                  <div
                    style={{
                      marginBottom: '1.5px'
                    }}
                    className="w-100 w-md-auto"
                  >
                    <Button
                      variant="falcon-primary"
                      type="submit"
                      className="px-3 d-flex align-items-center justify-content-center w-100 w-md-auto"
                      disabled={isSubmitting || isFormikSubmitting}
                    >
                      {isSubmitting || isFormikSubmitting ? (
                        <>
                          <Spinner
                            style={{
                              width: '16px',
                              height: '16px'
                            }}
                            className="me-1"
                          />
                          ارسال...
                        </>
                      ) : (
                        'ارسال'
                      )}
                    </Button>
                  </div>
                </Col>
              </Row>
            </Form>
          )}
        </Formik>
      </Col>
    </Row>
  );
};

export default AiKeywordsForm;
